import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, ImageIcon, Calendar, MapPin, Link, Upload, X, Sparkles } from "lucide-react";
import { useCategories } from "@/hooks/useCategories";

interface AddEventDialogProps {
  onEventAdded: () => void;
}

export const AddEventDialog = ({ onEventAdded }: AddEventDialogProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { categories } = useCategories();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fetchUrl, setFetchUrl] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    title_ar: "",
    description: "",
    description_ar: "",
    date: "",
    start_time: "",
    end_time: "",
    location: "",
    location_ar: "",
    image_url: "",
    external_link: "",
    category: "",
    source_name: "إضافة يدوية",
    is_published: true,
  });

  const resetForm = () => {
    setFormData({
      title: "",
      title_ar: "",
      description: "",
      description_ar: "",
      date: "",
      start_time: "",
      end_time: "",
      location: "",
      location_ar: "",
      image_url: "",
      external_link: "",
      category: "",
      source_name: "إضافة يدوية",
      is_published: true,
    });
    setUploadedImage(null);
    setImagePreview(null);
    setFetchUrl("");
  };

  const handleFetchEventData = async () => {
    if (!fetchUrl.trim()) {
      toast.error(isRTL ? "يرجى إدخال رابط الفعالية" : "Please enter event URL");
      return;
    }

    setIsFetching(true);

    try {
      const { data, error } = await supabase.functions.invoke("fetch-event-data", {
        body: { url: fetchUrl },
      });

      if (error) throw error;

      if (data?.success && data?.data) {
        const eventData = data.data;
        
        setFormData((prev) => ({
          ...prev,
          title: eventData.title || prev.title,
          title_ar: eventData.title_ar || prev.title_ar,
          description: eventData.description || prev.description,
          description_ar: eventData.description_ar || prev.description_ar,
          date: eventData.date || prev.date,
          location: eventData.location || prev.location,
          location_ar: eventData.location_ar || prev.location_ar,
          image_url: eventData.image_url || prev.image_url,
          external_link: eventData.external_link || prev.external_link,
        }));

        toast.success(isRTL ? "تم جلب البيانات بنجاح" : "Data fetched successfully");
      } else {
        throw new Error(data?.error || "Failed to fetch");
      }
    } catch (error: any) {
      console.error("Error fetching event data:", error);
      toast.error(isRTL ? "فشل في جلب البيانات" : "Failed to fetch event data");
    } finally {
      setIsFetching(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error(isRTL ? "يرجى اختيار ملف صورة" : "Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB");
      return;
    }

    setUploadedImage(file);
    setFormData({ ...formData, image_url: "" }); // Clear URL if uploading file
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `events/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("event-images")
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error:", uploadError);
      throw uploadError;
    }

    const { data } = supabase.storage
      .from("event-images")
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title && !formData.title_ar) {
      toast.error(isRTL ? "يرجى إدخال عنوان الفعالية" : "Please enter event title");
      return;
    }

    setIsSubmitting(true);

    try {
      let imageUrl = formData.image_url;

      // Upload image if file is selected
      if (uploadedImage) {
        setIsUploading(true);
        try {
          imageUrl = await uploadImage(uploadedImage) || "";
        } catch (error) {
          console.error("Image upload failed:", error);
          toast.error(isRTL ? "فشل في رفع الصورة" : "Failed to upload image");
          setIsSubmitting(false);
          setIsUploading(false);
          return;
        }
        setIsUploading(false);
      }

      const { error } = await supabase.from("scraped_events").insert({
        title: formData.title || formData.title_ar,
        title_ar: formData.title_ar || null,
        description: formData.description || null,
        description_ar: formData.description_ar || null,
        date: formData.date || null,
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        location: formData.location || null,
        location_ar: formData.location_ar || null,
        image_url: formData.image_url || null,
        external_link: formData.external_link || null,
        category: formData.category || null,
        source_name: formData.source_name || "إضافة يدوية",
        source_url: "manual-entry",
        is_published: formData.is_published,
        ticket_status: "available",
      });

      if (error) throw error;

      toast.success(isRTL ? "تم إضافة الفعالية بنجاح" : "Event added successfully");
      resetForm();
      setIsOpen(false);
      onEventAdded();
    } catch (error: any) {
      console.error("Error adding event:", error);
      toast.error(isRTL ? "حدث خطأ أثناء الإضافة" : "Error adding event");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          {isRTL ? "إضافة فعالية" : "Add Event"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {isRTL ? "إضافة فعالية جديدة" : "Add New Event"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* جلب البيانات تلقائياً */}
          <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="w-4 h-4 text-primary" />
              {isRTL ? "جلب البيانات تلقائياً (اختياري)" : "Auto-fetch Data (Optional)"}
            </Label>
            <p className="text-xs text-muted-foreground">
              {isRTL 
                ? "أدخل رابط صفحة الفعالية لجلب البيانات تلقائياً"
                : "Enter the event page URL to auto-fetch data"}
            </p>
            <div className="flex gap-2">
              <Input
                value={fetchUrl}
                onChange={(e) => setFetchUrl(e.target.value)}
                placeholder="https://example.com/event-page"
                dir="ltr"
                className="flex-1"
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleFetchEventData}
                disabled={isFetching || !fetchUrl.trim()}
                className="gap-2 shrink-0"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isRTL ? "جاري الجلب..." : "Fetching..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    {isRTL ? "جلب البيانات" : "Fetch Data"}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* العناوين */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? "العنوان (عربي)" : "Title (Arabic)"}</Label>
              <Input
                value={formData.title_ar}
                onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                placeholder={isRTL ? "عنوان الفعالية بالعربي" : "Event title in Arabic"}
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? "العنوان (إنجليزي)" : "Title (English)"}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={isRTL ? "عنوان الفعالية بالإنجليزي" : "Event title in English"}
                dir="ltr"
              />
            </div>
          </div>

          {/* الوصف */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isRTL ? "الوصف (عربي)" : "Description (Arabic)"}</Label>
              <Textarea
                value={formData.description_ar}
                onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                placeholder={isRTL ? "وصف الفعالية بالعربي" : "Event description in Arabic"}
                dir="rtl"
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? "الوصف (إنجليزي)" : "Description (English)"}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={isRTL ? "وصف الفعالية بالإنجليزي" : "Event description in English"}
                dir="ltr"
                rows={3}
              />
            </div>
          </div>

          {/* القسم */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {isRTL ? "القسم" : "Category"}
            </Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder={isRTL ? "اختر القسم" : "Select category"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="events">{isRTL ? "الفعاليات" : "Events"}</SelectItem>
                <SelectItem value="exhibitions">{isRTL ? "المعارض" : "Exhibitions"}</SelectItem>
                <SelectItem value="festivals">{isRTL ? "المهرجانات" : "Festivals"}</SelectItem>
                <SelectItem value="seasons">{isRTL ? "المواسم" : "Seasons"}</SelectItem>
                <SelectItem value="conferences">{isRTL ? "المؤتمرات والندوات" : "Conferences"}</SelectItem>
                <SelectItem value="occasions">{isRTL ? "المناسبات العامة" : "Public Occasions"}</SelectItem>
                {/* أقسام ديناميكية من قاعدة البيانات */}
                {categories
                  .filter((cat) => cat.category_slug && !["events", "exhibitions", "festivals", "seasons", "conferences", "occasions"].includes(cat.category_slug))
                  .map((cat) => (
                    <SelectItem key={cat.id} value={cat.category_slug || cat.path.replace("/", "")}>
                      {isRTL ? cat.name_ar : cat.name_en}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* التاريخ والوقت */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {isRTL ? "التاريخ" : "Date"}
              </Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? "وقت البداية" : "Start Time"}</Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{isRTL ? "وقت النهاية" : "End Time"}</Label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          </div>

          {/* الموقع */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {isRTL ? "الموقع (عربي)" : "Location (Arabic)"}
              </Label>
              <Input
                value={formData.location_ar}
                onChange={(e) => setFormData({ ...formData, location_ar: e.target.value })}
                placeholder={isRTL ? "موقع الفعالية بالعربي" : "Event location in Arabic"}
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {isRTL ? "الموقع (إنجليزي)" : "Location (English)"}
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={isRTL ? "موقع الفعالية بالإنجليزي" : "Event location in English"}
                dir="ltr"
              />
            </div>
          </div>

          {/* رفع الصورة أو رابط الصورة */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              {isRTL ? "صورة الفعالية" : "Event Image"}
            </Label>
            
            {/* رفع الصورة */}
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                id="event-image-upload"
              />
              
              {!uploadedImage && !formData.image_url && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isRTL ? "اضغط لرفع صورة (الحد الأقصى 5MB)" : "Click to upload image (max 5MB)"}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    PNG, JPG, WEBP
                  </p>
                </div>
              )}

              {/* معاينة الصورة المرفوعة */}
              {imagePreview && (
                <div className="relative inline-block">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-xs h-40 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                    onClick={removeUploadedImage}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    {uploadedImage?.name}
                  </p>
                </div>
              )}
            </div>

            {/* أو إدخال رابط */}
            {!uploadedImage && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{isRTL ? "أو" : "or"}</span>
                  <div className="flex-1 h-px bg-border" />
                </div>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder={isRTL ? "أدخل رابط الصورة" : "Enter image URL"}
                  dir="ltr"
                />
                {formData.image_url && (
                  <div className="relative inline-block">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full max-w-xs h-40 object-cover rounded-lg border"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full"
                      onClick={() => setFormData({ ...formData, image_url: "" })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* الرابط الخارجي */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              {isRTL ? "رابط التذاكر / الموقع الخارجي" : "Tickets / External Link"}
            </Label>
            <Input
              value={formData.external_link}
              onChange={(e) => setFormData({ ...formData, external_link: e.target.value })}
              placeholder="https://example.com/tickets"
              dir="ltr"
            />
          </div>

          {/* مصدر الفعالية */}
          <div className="space-y-2">
            <Label>{isRTL ? "اسم المصدر" : "Source Name"}</Label>
            <Input
              value={formData.source_name}
              onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
              placeholder={isRTL ? "مثال: الهيئة العامة للترفيه" : "e.g., General Entertainment Authority"}
            />
          </div>

          {/* النشر مباشرة */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
            <div>
              <Label className="text-base">{isRTL ? "نشر مباشرة" : "Publish Immediately"}</Label>
              <p className="text-sm text-muted-foreground">
                {isRTL
                  ? "سيتم نشر الفعالية فور الإضافة"
                  : "Event will be published immediately after adding"}
              </p>
            </div>
            <Switch
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
            />
          </div>

          {/* أزرار الإجراء */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetForm();
                setIsOpen(false);
              }}
            >
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
            <Button type="submit" disabled={isSubmitting || isUploading} className="gap-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isUploading 
                    ? (isRTL ? "جاري رفع الصورة..." : "Uploading image...")
                    : (isRTL ? "جاري الإضافة..." : "Adding...")}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {isRTL ? "إضافة الفعالية" : "Add Event"}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
